import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/logo";
import { getPerformanceStats } from "@/lib/performance.functions";

export const Route = createFileRoute("/performance")({
  head: () => ({
    meta: [
      { title: "Historical Market Intelligence Performance — ForexMind AI" },
      {
        name: "description",
        content:
          "Public track record of ForexMind AI market intelligence: total ideas, resolved ideas, historical accuracy, average R:R, best and worst pair.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "Historical Market Intelligence Performance — ForexMind AI" },
      {
        property: "og:description",
        content:
          "Aggregate outcomes for ForexMind AI market intelligence — for transparency, not financial advice.",
      },
      { property: "og:url", content: "https://forexmind.ai/performance" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Historical Market Intelligence Performance — ForexMind AI" },
      {
        name: "twitter:description",
        content:
          "Aggregate outcomes for ForexMind AI market intelligence — for transparency, not financial advice.",
      },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [{ rel: "canonical", href: "https://forexmind.ai/performance" }],
  }),
  component: PerformancePage,
});

function PerformancePage() {
  const fetchStats = useServerFn(getPerformanceStats);
  const { data, isLoading } = useQuery({
    queryKey: ["public", "performance"],
    queryFn: () => fetchStats(),
    refetchInterval: 60_000,
  });

  const updated = data?.updatedAt ? new Date(data.updatedAt).toLocaleString() : "—";

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 py-5">
        <Link to="/">
          <Logo />
        </Link>
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to home
        </Link>
      </header>

      <section className="mx-auto max-w-3xl px-6 pt-6 pb-16">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-widest text-primary">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-buy" />
          Live track record
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl">
          ForexMind AI — Historical Market Intelligence Performance
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Aggregated outcomes for all market intelligence ideas. Updated: {updated}.{" "}
          <Link to="/performance-methodology" className="underline hover:text-foreground">
            See methodology
          </Link>
          .
        </p>

        <Card className="mt-8 border-border/60 bg-card/80 p-6 backdrop-blur">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading aggregate stats…</p>
          ) : (
            <div className="divide-y divide-border/60">
              <Row label="Total market intelligence ideas" value={data?.total ?? 0} />
              <Row label="Resolved ideas" value={data?.resolved ?? 0} />
              <Row
                label="Historical accuracy (BUY/SELL only)"
                value={`${data?.winRatePct ?? 0}%`}
                accent
              />
              <Row label="Take-profit outcomes" value={data?.tpHits ?? 0} />
              <Row label="Stop-loss outcomes" value={data?.slHits ?? 0} />
              <Row label="Expired ideas" value={data?.expired ?? 0} />
              <Row label="Avg R:R achieved" value={`1 : ${(data?.avgRR ?? 0).toFixed(2)}`} />
              <Row label="Avg conviction score" value={`${data?.avgConviction ?? 0} / 100`} />
              <Row
                label="Avg drawdown"
                value={
                  data?.avgDrawdownPct == null ? "—" : `${data.avgDrawdownPct.toFixed(2)}%`
                }
              />
              <Row label="Best performing pair" value={data?.bestPair ?? "—"} />
              <Row label="Worst performing pair" value={data?.worstPair ?? "—"} />
            </div>
          )}
        </Card>

        <div className="mt-6 rounded-lg border border-warn/30 bg-warn/10 p-4 text-[12px] leading-snug text-warn">
          <ul className="space-y-1">
            <li>* WAIT / neutral views are excluded from historical accuracy calculations.</li>
            <li>* Past performance does not guarantee future results.</li>
            <li>
              * Historical market intelligence is provided for transparency only and should not
              be interpreted as investment advice.
            </li>
            <li>
              * ForexMind AI does not guarantee accuracy, profit, or trading outcomes.
            </li>
          </ul>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          See the full{" "}
          <Link to="/risk-disclosure" className="underline hover:text-foreground">
            risk disclosure
          </Link>{" "}
          and{" "}
          <Link to="/performance-methodology" className="underline hover:text-foreground">
            methodology
          </Link>
          .
        </p>
      </section>
    </div>
  );
}

function Row({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span
        className={`font-mono text-base font-semibold ${accent ? "text-primary" : "text-foreground"}`}
      >
        {value}
      </span>
    </div>
  );
}
